import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useParams } from "wouter";
import { Loader2, Users, CheckCircle2, Scissors, Clock, Wifi, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getQueuePanelProfile } from "@shared/queuePanelProfiles";
import { ProductSalesPanel } from "@/components/ProductSalesPanel";

// Página pública para clientes verificarem sua posição na fila
// URL: /fila/:salonId
export default function QueueStatusPage() {
  const params = useParams<{ salonId: string }>();
  const salonId = parseInt(params.salonId ?? "0", 10);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const { data: salon } = trpc.salon.getPublicInfo.useQuery(
    { salonId } as any,
    { enabled: !!salonId, retry: false }
  );
  const isStorePanel = (salon as any)?.queuePanelProfile === "store";

  const { data: queue, isLoading } = trpc.queue.listPublic.useQuery(
    { salonId },
    {
      enabled: !!salonId && !isStorePanel,
      refetchInterval: 5000, // Atualiza a cada 5s
    }
  );

  const salonColor = "#C9A96E";
  const salonName = (salon as any)?.name ?? "Salão";
  const logoUrl = (salon as any)?.logoUrl;
  const panelProfile = getQueuePanelProfile((salon as any)?.queuePanelProfile);

  const waiting = queue?.filter((q: any) => q.status === "waiting") ?? [];
  const preparing = queue?.filter((q: any) => q.status === "preparing") ?? [];
  const called = queue?.filter((q: any) => q.status === "called") ?? [];
  const inService = queue?.filter((q: any) => q.status === "in_service") ?? [];

  if (!salonId || isNaN(salonId)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0a0a0f 0%, #111118 100%)" }}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium">Link inválido</p>
          <p className="text-white/40 text-sm mt-1">O código do salão não foi encontrado.</p>
        </div>
      </div>
    );
  }

  if (isStorePanel) {
    return (
      <ProductSalesPanel
        salonSlug={(salon as any)?.slug ?? ""}
        salonName={salonName}
        salonColor={salonColor}
        logoUrl={logoUrl}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(135deg, #0a0a0f 0%, #111118 60%, #0d0d15 100%)" }}>

      {/* Header */}
      <div className="border-b border-white/5 px-5 py-4 flex items-center justify-between"
        style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(8px)" }}>
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={salonName}
              className="w-10 h-10 rounded-xl object-cover border border-white/10"
              style={{ boxShadow: `0 0 12px ${salonColor}40` }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border border-white/10"
              style={{ background: `${salonColor}20` }}
            >
              <Scissors className="w-5 h-5" style={{ color: salonColor }} />
            </div>
          )}
          <div>
            <h1 className="text-base font-bold text-white">{salonName}</h1>
            <p className="text-xs text-white/40 flex items-center gap-1">
              <Wifi className="w-3 h-3" />
              Fila de Atendimento
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-white tabular-nums">
            {format(currentTime, "HH:mm", { locale: ptBR })}
          </p>
          <p className="text-xs text-white/30 capitalize">
            {format(currentTime, "EEE, dd/MM", { locale: ptBR })}
          </p>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 px-4 py-6 max-w-lg mx-auto w-full space-y-5">

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-white/30" />
          </div>
        ) : (
          <>
            {preparing.length > 0 && (
              <div className="rounded-2xl border border-violet-400/20 p-5 text-center" style={{ background: "rgba(139,92,246,0.09)" }}>
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-violet-400/15">
                  <Scissors className="h-5 w-5 text-violet-200" />
                </div>
                <p className="mt-3 text-sm font-semibold text-violet-100">{panelProfile.preparationTitle}</p>
                <p className="mt-1 text-xs leading-relaxed text-white/55">{panelProfile.preparationDescription}</p>
                <p className="mt-3 text-xs text-violet-100/70">{panelProfile.preparationTip}</p>
              </div>
            )}

            {/* Sendo chamado agora */}
            {called.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: salonColor }} />
                  <h2 className="text-xs font-semibold uppercase tracking-widest text-white/50">
                    Chamando agora
                  </h2>
                </div>
                <div className="space-y-2">
                  {called.map((item: any) => (
                    <div
                      key={item.id}
                      className="rounded-2xl p-5 border-2"
                      style={{
                        borderColor: salonColor,
                        background: `linear-gradient(135deg, ${salonColor}18, ${salonColor}08)`,
                        boxShadow: `0 0 30px ${salonColor}20`,
                      }}
                    >
                      {item.ticketNumber && (
                        <span
                          className="inline-block text-xl font-black px-3 py-0.5 rounded-lg border-2 tracking-widest mb-2"
                          style={{ color: salonColor, borderColor: salonColor, background: `${salonColor}20` }}
                        >
                          {item.ticketNumber}
                        </span>
                      )}
                      <p className="text-3xl font-bold text-white">{item.clientName}</p>
                      <p className="text-base mt-1 font-medium" style={{ color: salonColor }}>{item.serviceName}</p>
                      <p className="text-sm text-white/40 mt-1">
                         {(item as any).stationName ? (
                           <>
                             Dirija-se à:{" "}
                             <span className="text-white font-bold text-base">{(item as any).stationName}</span>
                           </>
                         ) : (
                           <>
                             Profissional:{" "}
                             <span className="text-white/70 font-medium">{item.professionalName}</span>
                           </>
                         )}
                       </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Em atendimento */}
            {inService.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <h2 className="text-xs font-semibold uppercase tracking-widest text-white/50">
                    Em atendimento ({inService.length})
                  </h2>
                </div>
                <div className="space-y-2">
                  {inService.map((item: any) => (
                    <div
                      key={item.id}
                      className="rounded-xl p-4 border border-emerald-900/40 flex items-center gap-3"
                      style={{ background: "rgba(16,185,129,0.06)" }}
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-white truncate">{item.clientName}</p>
                        <p className="text-xs text-emerald-400 truncate">{item.serviceName} · {item.professionalName}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fila de espera */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <h2 className="text-xs font-semibold uppercase tracking-widest text-white/50">
                  Aguardando ({waiting.length})
                </h2>
              </div>

              {waiting.length === 0 ? (
                <div
                  className="rounded-xl border border-dashed border-white/5 p-10 text-center"
                  style={{ background: "rgba(255,255,255,0.02)" }}
                >
                  <CheckCircle2 className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 font-medium">Fila vazia</p>
                  <p className="text-white/15 text-sm mt-1">Nenhum cliente aguardando no momento.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {waiting.map((item: any, idx: number) => (
                    <div
                      key={item.id}
                      className="rounded-xl p-4 border border-white/5 flex items-center gap-3"
                      style={{ background: "rgba(255,255,255,0.03)" }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                        style={{
                          background: `${salonColor}20`,
                          border: `1.5px solid ${salonColor}60`,
                          color: salonColor,
                        }}
                      >
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-white truncate">{item.clientName}</p>
                        <p className="text-xs text-white/30 truncate">{item.serviceName}</p>
                      </div>
                      {idx === 0 && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: `${salonColor}25`, color: salonColor }}
                        >
                          Próximo
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Aviso de atualização automática */}
            <div className="text-center pt-2">
              <div className="inline-flex items-center gap-2 text-xs text-white/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Atualiza automaticamente a cada 5 segundos
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        className="border-t border-white/5 px-5 py-3 text-center"
        style={{ background: "rgba(255,255,255,0.02)" }}
      >
        <p className="text-xs text-white/15">
          {salonName} · Painel de Fila Digital
        </p>
      </div>
    </div>
  );
}
